<script setup>
/**
 * BillingProfileForm.vue
 *
 * Exibido entre a escolha do plano (PlanCatalogByName) e o pagamento
 * (SubscriptionMPForm). Coleta os dados fiscais do usuário para emissão
 * da NFS-e via FOCUS NFe.
 */

import { ref, onMounted, computed } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { storeToRefs } from "pinia";
import {
  fetchBillingProfile,
  saveBillingProfile,
} from "@/services/billingProfileService";

const emit = defineEmits(["confirmed", "back"]);

const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

// ── Estado geral ────────────────────────────────────────────────────────────
const loading     = ref(true);
const saving      = ref(false);
const loadError   = ref("");
const genericError = ref(""); // erros sem campo mapeado
const fieldErrors  = ref({}); // { nomeCampo: "frase de erro" }

// ── Seções colapsáveis ──────────────────────────────────────────────────────
const sectionIdentOpen = ref(true);
const sectionAddrOpen  = ref(false);

// ── Modelo do formulário ────────────────────────────────────────────────────
const form = ref({
  documentType:          "CPF",
  documentNumber:        "",
  legalName:             "",
  tradeName:             "",
  email:                 "",
  phone:                 "",
  street:                "",
  number:                "",
  complement:            "",
  neighborhood:          "",
  city:                  "",
  state:                 "",
  zipCode:               "",
  municipalRegistration: "",
});

const isCnpj = computed(() => form.value.documentType === "CNPJ");

// ── Mapeamento backend → campo do form ──────────────────────────────────────
// Chaves em lowercase para comparação case-insensitive
const FIELD_MAP = {
  documenttype:          "documentType",
  documentnumber:        "documentNumber",
  legalname:             "legalName",
  tradename:             "tradeName",
  email:                 "email",
  phone:                 "phone",
  street:                "street",
  number:                "number",
  complement:            "complement",
  neighborhood:          "neighborhood",
  city:                  "city",
  state:                 "state",
  zipcode:               "zipCode",
  municipalregistration: "municipalRegistration",
};

// Campos que pertencem a cada seção (para abri-la automaticamente)
const IDENT_FIELDS = new Set(["documentType","documentNumber","legalName",
                               "tradeName","email","phone"]);
const ADDR_FIELDS  = new Set(["street","number","complement","neighborhood",
                               "city","state","zipCode","municipalRegistration"]);

// ── Máscaras ────────────────────────────────────────────────────────────────
function maskCpf(v) {
  return v.replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .slice(0, 14);
}

function maskCnpj(v) {
  return v.replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
    .slice(0, 18);
}

function maskCep(v) {
  return v.replace(/\D/g, "")
    .replace(/(\d{5})(\d{1,3})$/, "$1-$2")
    .slice(0, 9);
}

function onDocumentInput(e) {
  form.value.documentNumber = isCnpj.value
    ? maskCnpj(e.target.value)
    : maskCpf(e.target.value);
  fieldErrors.value.documentNumber = "";
}

function onCepInput(e) {
  form.value.zipCode = maskCep(e.target.value);
  fieldErrors.value.zipCode = "";
  if (form.value.zipCode.replace(/\D/g, "").length === 8) {
    fetchAddressByCep();
  }
}

function onDocumentTypeChange() {
  form.value.documentNumber = "";
  fieldErrors.value.documentNumber = "";
}

// ── ViaCEP ──────────────────────────────────────────────────────────────────
async function fetchAddressByCep() {
  const cep = form.value.zipCode.replace(/\D/g, "");
  if (cep.length !== 8) return;
  try {
    const res  = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await res.json();
    if (data.erro) return;
    form.value.street       = data.logradouro || form.value.street;
    form.value.neighborhood = data.bairro     || form.value.neighborhood;
    form.value.city         = data.localidade || form.value.city;
    form.value.state        = data.uf         || form.value.state;
    sectionAddrOpen.value = true;
  } catch {
    // silencioso — usuário preenche manualmente
  }
}

// ── Carrega perfil existente ─────────────────────────────────────────────────
onMounted(async () => {
  if (currentUser.value?.email) {
    form.value.email = currentUser.value.email;
  }

  try {
    const res = await fetchBillingProfile();
    const p   = res.data;

    form.value = {
      documentType:          p.documentType         || "CPF",
      documentNumber:        p.documentNumber        || "",
      legalName:             p.legalName             || "",
      tradeName:             p.tradeName             || "",
      email:                 p.email                 || currentUser.value?.email || "",
      phone:                 p.phone                 || "",
      street:                p.street                || "",
      number:                p.number                || "",
      complement:            p.complement            || "",
      neighborhood:          p.neighborhood          || "",
      city:                  p.city                  || "",
      state:                 p.state                 || "",
      zipCode:               p.zipCode               || "",
      municipalRegistration: p.municipalRegistration || "",
    };

    sectionAddrOpen.value = true;
  } catch (err) {
    if (err?.response?.status !== 404) {
      loadError.value = "Não foi possível carregar seus dados fiscais. Preencha manualmente.";
    }
  } finally {
    loading.value = false;
  }
});

// ── Submissão ────────────────────────────────────────────────────────────────
async function submit() {
  fieldErrors.value  = {};
  genericError.value = "";
  saving.value       = true;

  try {
    await saveBillingProfile({
      documentType:          form.value.documentType,
      documentNumber:        form.value.documentNumber,
      legalName:             form.value.legalName.trim(),
      tradeName:             form.value.tradeName?.trim()             || null,
      email:                 form.value.email.trim(),
      phone:                 form.value.phone?.trim()                 || null,
      street:                form.value.street.trim(),
      number:                form.value.number.trim(),
      complement:            form.value.complement?.trim()            || null,
      neighborhood:          form.value.neighborhood.trim(),
      city:                  form.value.city.trim(),
      state:                 form.value.state.trim().toUpperCase(),
      zipCode:               form.value.zipCode.trim(),
      municipalRegistration: form.value.municipalRegistration?.trim() || null,
    });

    emit("confirmed");
  } catch (err) {
    const data    = err?.response?.data;
    const errKeys = Object.keys(data?.errors || {});
    const mapped  = {};
    const unmapped = [];

    if (errKeys.length > 0) {
      for (const key of errKeys) {
        const formField = FIELD_MAP[key.toLowerCase()];
        const messages  = data.errors[key];
        const msg       = Array.isArray(messages) ? messages[0] : messages;

        if (formField) {
          mapped[formField] = msg;
        } else if (msg) {
          unmapped.push(msg);
        }
      }
    } else if (typeof data?.message === "string") {
      unmapped.push(data.message);
    } else {
      unmapped.push("Não foi possível salvar. Verifique os dados e tente novamente.");
    }

    fieldErrors.value  = mapped;
    genericError.value = unmapped.join(" ");

    // Abre automaticamente a seção com erros
    if (Object.keys(mapped).some(k => IDENT_FIELDS.has(k))) sectionIdentOpen.value = true;
    if (Object.keys(mapped).some(k => ADDR_FIELDS.has(k)))  sectionAddrOpen.value  = true;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="billing-form-wrapper">

    <!-- Cabeçalho -->
    <div class="billing-header">
      <h2 class="billing-title">Dados para Nota Fiscal</h2>
      <p class="billing-subtitle">
        Esses dados serão usados para emitir sua Nota Fiscal de Serviço (NFS-e).
        A nota será enviada por e-mail após a confirmação do pagamento.
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="billing-loading">
      <svg class="spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10"
          stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
      <span>Carregando seus dados...</span>
    </div>

    <form v-else @submit.prevent="submit" novalidate>

      <!-- Aviso de erro de carregamento -->
      <div v-if="loadError" class="alert alert-warn">{{ loadError }}</div>

      <!-- ══════════════════════════════════════════════════════
           SEÇÃO 1 — IDENTIFICAÇÃO
      ══════════════════════════════════════════════════════ -->
      <div class="collapsible" :class="{ open: sectionIdentOpen }">
        <button type="button" class="collapsible-header"
          @click="sectionIdentOpen = !sectionIdentOpen"
          :aria-expanded="sectionIdentOpen">
          <span class="collapsible-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </span>
          <span class="collapsible-label">Identificação</span>
          <span class="collapsible-chevron">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </span>
        </button>

        <div class="collapsible-body">

          <div class="form-group">
            <label class="form-label">Tipo de Documento *</label>
            <div class="radio-group">
              <label class="radio-option">
                <input type="radio" v-model="form.documentType"
                  value="CPF" @change="onDocumentTypeChange"/>
                <span>CPF (Pessoa Física)</span>
              </label>
              <label class="radio-option">
                <input type="radio" v-model="form.documentType"
                  value="CNPJ" @change="onDocumentTypeChange"/>
                <span>CNPJ (Pessoa Jurídica)</span>
              </label>
            </div>
            <p v-if="fieldErrors.documentType" class="field-error">
              <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.documentType }}
            </p>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">{{ isCnpj ? "CNPJ" : "CPF" }} *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.documentNumber }"
                type="text"
                :placeholder="isCnpj ? '00.000.000/0001-00' : '000.000.000-00'"
                :value="form.documentNumber"
                @input="onDocumentInput"
                required
              />
              <p v-if="fieldErrors.documentNumber" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.documentNumber }}
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">
                {{ isCnpj ? "Razão Social" : "Nome Completo" }} *
              </label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.legalName }"
                type="text"
                v-model="form.legalName"
                :placeholder="isCnpj ? 'Empresa Ltda.' : 'Seu nome completo'"
                @input="fieldErrors.legalName = ''"
                required
              />
              <p v-if="fieldErrors.legalName" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.legalName }}
              </p>
            </div>
          </div>

          <div class="form-group" v-if="isCnpj">
            <label class="form-label">Nome Fantasia</label>
            <input
              class="form-input"
              :class="{ 'form-input--error': fieldErrors.tradeName }"
              type="text"
              v-model="form.tradeName"
              placeholder="Nome fantasia (opcional)"
              @input="fieldErrors.tradeName = ''"
            />
            <p v-if="fieldErrors.tradeName" class="field-error">
              <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.tradeName }}
            </p>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">E-mail para receber a NF *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.email }"
                type="email"
                v-model="form.email"
                placeholder="email@exemplo.com"
                @input="fieldErrors.email = ''"
                required
              />
              <p v-if="fieldErrors.email" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.email }}
              </p>
              <p v-else class="form-hint">
                A FOCUS NFe enviará o PDF da nota para este endereço.
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">Telefone</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.phone }"
                type="tel"
                v-model="form.phone"
                placeholder="(00) 00000-0000"
                @input="fieldErrors.phone = ''"
              />
              <p v-if="fieldErrors.phone" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.phone }}
              </p>
            </div>
          </div>

        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════
           SEÇÃO 2 — ENDEREÇO
      ══════════════════════════════════════════════════════ -->
      <div class="collapsible" :class="{ open: sectionAddrOpen }">
        <button type="button" class="collapsible-header"
          @click="sectionAddrOpen = !sectionAddrOpen"
          :aria-expanded="sectionAddrOpen">
          <span class="collapsible-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </span>
          <span class="collapsible-label">Endereço</span>
          <span class="collapsible-chevron">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </span>
        </button>

        <div class="collapsible-body">

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">CEP *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.zipCode }"
                type="text"
                :value="form.zipCode"
                @input="onCepInput"
                placeholder="00000-000"
                maxlength="9"
                required
              />
              <p v-if="fieldErrors.zipCode" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.zipCode }}
              </p>
              <p v-else class="form-hint">Preenche o endereço automaticamente.</p>
            </div>

            <div class="form-group">
              <label class="form-label">Número *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.number }"
                type="text"
                v-model="form.number"
                placeholder="123"
                @input="fieldErrors.number = ''"
                required
              />
              <p v-if="fieldErrors.number" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.number }}
              </p>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Logradouro *</label>
            <input
              class="form-input"
              :class="{ 'form-input--error': fieldErrors.street }"
              type="text"
              v-model="form.street"
              placeholder="Rua, Avenida, etc."
              @input="fieldErrors.street = ''"
              required
            />
            <p v-if="fieldErrors.street" class="field-error">
              <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.street }}
            </p>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Complemento</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.complement }"
                type="text"
                v-model="form.complement"
                placeholder="Apto, Sala (opcional)"
                @input="fieldErrors.complement = ''"
              />
              <p v-if="fieldErrors.complement" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.complement }}
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">Bairro *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.neighborhood }"
                type="text"
                v-model="form.neighborhood"
                placeholder="Centro"
                @input="fieldErrors.neighborhood = ''"
                required
              />
              <p v-if="fieldErrors.neighborhood" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.neighborhood }}
              </p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Cidade *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.city }"
                type="text"
                v-model="form.city"
                placeholder="Maceió"
                @input="fieldErrors.city = ''"
                required
              />
              <p v-if="fieldErrors.city" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.city }}
              </p>
            </div>

            <div class="form-group form-group--sm">
              <label class="form-label">Estado *</label>
              <input
                class="form-input"
                :class="{ 'form-input--error': fieldErrors.state }"
                type="text"
                v-model="form.state"
                placeholder="AL"
                maxlength="2"
                @input="fieldErrors.state = ''"
                required
              />
              <p v-if="fieldErrors.state" class="field-error">
                <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.state }}
              </p>
            </div>
          </div>

          <div class="form-group" v-if="isCnpj">
            <label class="form-label">Inscrição Municipal</label>
            <input
              class="form-input"
              :class="{ 'form-input--error': fieldErrors.municipalRegistration }"
              type="text"
              v-model="form.municipalRegistration"
              placeholder="Opcional para pessoa jurídica"
              @input="fieldErrors.municipalRegistration = ''"
            />
            <p v-if="fieldErrors.municipalRegistration" class="field-error">
              <i class="fas fa-exclamation-circle"></i> {{ fieldErrors.municipalRegistration }}
            </p>
          </div>

        </div>
      </div>

      <!-- ── Erro genérico (sem campo mapeado) ─────────────────────────── -->
      <div v-if="genericError" class="alert alert-error">
        {{ genericError }}
      </div>

      <!-- ── Ações ──────────────────────────────────────────────────── -->
      <div class="form-actions">
        <button type="button" class="btn btn-secondary"
          :disabled="saving" @click="$emit('back')">
          Voltar
        </button>
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <svg v-if="saving" class="spin btn-spinner"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10"
              stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          {{ saving ? "Salvando..." : "Continuar para Pagamento" }}
        </button>
      </div>

    </form>
  </div>
</template>

<style scoped>
.billing-form-wrapper {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  padding: 0 4px;
}

/* ── Cabeçalho ── */
.billing-header { margin-bottom: 24px; }

.billing-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0 0 6px;
}

.billing-subtitle {
  font-size: 0.9rem;
  color: var(--text-light);
  margin: 0;
  line-height: 1.5;
}

/* ── Loading ── */
.billing-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: var(--text-light);
  font-size: 0.9rem;
}

/* ── Alertas ── */
.alert {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.875rem;
  margin-bottom: 16px;
  line-height: 1.6;
}

.alert-warn {
  background: var(--warning-bg);
  border: 1px solid var(--warning);
  color: var(--warning-text);
}

.alert-error {
  background: var(--danger-bg);
  border: 1px solid #f87171;
  color: var(--danger-text);
}

.alert-title {
  font-weight: 700;
  margin: 0 0 6px;
}

.alert-list {
  margin: 0;
  padding-left: 18px;
}

.alert-list li { margin-bottom: 3px; }

/* ── Seções colapsáveis ── */
.collapsible {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.collapsible.open {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.collapsible-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: var(--bg-content);
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.collapsible-header:hover {
  background: var(--surface-hover);
}

.collapsible-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--brand);
}

.collapsible-icon svg { width: 18px; height: 18px; }

.collapsible-label {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
}

.collapsible-chevron {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-light);
  transition: transform 0.25s ease;
}

.collapsible-chevron svg { width: 18px; height: 18px; }

.collapsible.open .collapsible-chevron { transform: rotate(180deg); }

.collapsible-body {
  display: none;
  padding: 16px 16px 4px;
  background: var(--bg-main);
}

.collapsible.open .collapsible-body { display: block; }

.form-group {
  flex: 1;
  margin-bottom: 14px;
}

.form-group--sm { flex: 0 0 90px; }

.form-row {
  display: flex;
  gap: 14px;
}

/* ── Estado de erro no input ── */
.form-input--error {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.18);
}

.form-input--error:focus {
  border-color: var(--danger);
  box-shadow: var(--focus-ring-danger);
}

/* ── Mensagem de erro inline ── */
.field-error {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.775rem;
  color: var(--danger-text);
  margin: 2px 0 0;
  line-height: 1.35;
}

.field-error i {
  font-size: 0.72rem;
  flex-shrink: 0;
}

.form-hint {
  font-size: 0.775rem;
  color: var(--text-light);
  margin: 2px 0 0;
}

/* ── Radio ── */
.radio-group { display: flex; gap: 20px; flex-wrap: wrap; }

.radio-option {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.9rem;
  cursor: pointer;
  color: var(--text-dark);
}

.radio-option input[type="radio"] {
  accent-color: var(--brand);
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* ── Ações ── */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn {
  min-width: 120px;
}

/* ── Spinner ── */
.spin { width: 20px; height: 20px; animation: spin 0.8s linear infinite; }
.btn-spinner { width: 18px; height: 18px; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsivo ── */
@media (max-width: 560px) {
  .form-row { flex-direction: column; gap: 0; }
  .form-group--sm { flex: 1; }
  .form-actions { flex-direction: row; justify-content: flex-end; }
  .btn { min-width: 100px; padding: 0 16px; }
}
</style>